import { useRef, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@jumpstone/ui-react';
import { copyTextToClipboard } from '../../lib/clipboard';

/**
 * Reveal-once dialog for the invite link `createEssInvite` returns ONLY when
 * its email could not be sent (`email_sent: false`). Says out loud that the
 * email did not go, so the employer never assumes the employee was notified,
 * and offers a copy affordance with a manual-select fallback.
 *
 * The raw link lives only in the caller's state: the server persists just
 * the token hash and `listEssInvites` never projects it, so once the caller
 * drops it the only way back is a fresh invite. Shared by the directory, the
 * add-employee flow, and the profile access tab.
 */
export function EssInviteLinkDialog({
  link,
  open,
  onOpenChange,
}: {
  link: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [copied, set_copied] = useState(false);
  const [copy_failed, set_copy_failed] = useState(false);
  const link_element_ref = useRef<HTMLDivElement | null>(null);

  async function copy_link(): Promise<void> {
    if (link === null) return;
    const ok = await copyTextToClipboard(link, link_element_ref.current);
    set_copied(ok);
    set_copy_failed(!ok);
  }

  // Every close path (Close button, Escape, overlay) resets the copy state,
  // so a later link never opens already reading "Copied".
  function handle_open_change(next: boolean): void {
    set_copied(false);
    set_copy_failed(false);
    onOpenChange(next);
  }

  return (
    <Dialog open={open && link !== null} onOpenChange={handle_open_change}>
      <DialogContent className="max-w-md">
        <DialogTitle>{t('employees.ess_invite_sent_title')}</DialogTitle>
        <DialogDescription>
          {t('employees.ess_invite_link_fallback_desc')}
        </DialogDescription>
        <p className="mt-2 nv-help is-error" role="alert">
          {t('employees.ess_invite_email_failed')}
        </p>
        {link !== null && (
          <div
            ref={link_element_ref}
            className="mt-3 nv-input select-all"
            style={{ wordBreak: 'break-all' }}
          >
            {link}
          </div>
        )}
        {copy_failed && (
          <p className="nv-help is-error mt-2" role="alert">
            {t('employees.ess_copy_link_failed')}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2.5">
          <Button
            variant="secondary"
            onClick={() => void copy_link()}
            data-testid="ess-copy-link"
          >
            {copied
              ? t('employees.ess_link_copied')
              : t('employees.ess_copy_link')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handle_open_change(false);
            }}
            data-testid="ess-dialog-close"
          >
            {t('common.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
